import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { editResume, getResumeDetails } from "../../slice/workerResumeSlice";

const WorkerEditResume = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const token = localStorage.getItem("token");

    const { resumeDetails, loading, error } = useSelector(
        (state) => state.workerResume
    );

    const [form, setForm] = useState({
        title: "",
        headline: "",
        summary: "",
        skills: "",
        is_default: 1,
        experience: [],
        education: [],
    });

    /* ================= FETCH DETAILS ================= */
    useEffect(() => {
        dispatch(getResumeDetails(id));
    }, [id, dispatch]);

    /* ================= AUTOFILL FORM ================= */
    useEffect(() => {
        if (resumeDetails) {
            setForm({
                title: resumeDetails.title || "",
                headline: resumeDetails.headline || "",
                summary: resumeDetails.summary || "",
                skills: resumeDetails.skills || "",
                is_default: resumeDetails.is_default || 1,

                experience: resumeDetails.experience?.length
                    ? resumeDetails.experience.map(exp => ({ ...exp }))
                    : [
                        {
                            company: "",
                            job_title: "",
                            start_date: "",
                            end_date: "",
                            description: "",
                        },
                    ],

                education: resumeDetails.education?.length
                    ? resumeDetails.education.map(edu => ({ ...edu }))
                    : [
                        {
                            school_or_cert: "",
                            degree: "",
                            field: "",
                            start_year: "",
                            end_year: "",
                            description: "",
                        },
                    ],
            });
        }
    }, [resumeDetails]);

    /* ================= HANDLERS ================= */
    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleExperienceChange = (index, e) => {
        setForm(prev => ({
            ...prev,
            experience: prev.experience.map((item, i) =>
                i === index
                    ? { ...item, [e.target.name]: e.target.value }
                    : item
            ),
        }));
    };


    const handleEducationChange = (index, e) => {
        setForm(prev => ({
            ...prev,
            education: prev.education.map((item, i) =>
                i === index
                    ? { ...item, [e.target.name]: e.target.value }
                    : item
            ),
        }));
    };

    // Add new empty experience
    const addExperience = () => {
        setForm(prev => ({
            ...prev,
            experience: [
                ...prev.experience,
                { id: "", company: "", job_title: "", start_date: "", end_date: "", description: "" } // id: "" added
            ]
        }));
    };

    const removeExperience = (index) => {
        setForm((prevForm) => ({
            ...prevForm,
            experience: prevForm.experience.filter((_, i) => i !== index),
        }));
    };

    // Add new empty education
    const addEducation = () => {
        setForm(prev => ({
            ...prev,
            education: [
                ...prev.education,
                { id: "", school_or_cert: "", degree: "", field: "", start_year: "", end_year: "", description: "" } // id: "" added
            ]
        }));
    };

    const removeEducation = (index) => {
        setForm((prevForm) => ({
            ...prevForm,
            education: prevForm.education.filter((_, i) => i !== index),
        }));
    };

    const isValidExperience = () => {
        return form.experience.some((exp, index) => {
            return exp.company && exp.job_title && exp.start_date;
        });
    };

    const isValidEducation = () => {
        return form.education.some((edu) => {
            return edu.school_or_cert && edu.degree && edu.start_year;
        });
    };

    const checkValidation = () => {
        if (!form.title) return toast.error("Please enter title"), false;
        if (!form.headline) return toast.error("Please enter headline"), false;
        if (!form.summary) return toast.error("Please enter summary"), false;
        if (!form.skills) return toast.error("Please enter skills"), false;

        if (!isValidExperience())
            return toast.error("Please add at least one valid experience"), false;

        if (!isValidEducation())
            return toast.error("Please add at least one valid education"), false;

        return true;
    };

    /* ================= SUBMIT ================= */
    const handleSubmit = (e) => {
        e.preventDefault();

        if (!checkValidation()) return;

        const formData = new FormData();

        // ✅ REQUIRED BY BACKEND
        formData.append("resume_id", id);

        formData.append("title", form.title);
        formData.append("headline", form.headline);
        formData.append("summary", form.summary);
        formData.append("skills", form.skills);
        formData.append("is_default", form.is_default);

        // ===== EXPERIENCE =====
        form.experience.forEach((exp, index) => {
            if (exp.id !== "") {
                formData.append(`experience[${index}][id]`, exp.id);
            }
            formData.append(`experience[${index}][company]`, exp.company);
            formData.append(`experience[${index}][job_title]`, exp.job_title);
            formData.append(`experience[${index}][start_date]`, exp.start_date);
            formData.append(`experience[${index}][end_date]`, exp.end_date || "");
            formData.append(`experience[${index}][description]`, exp.description);
        });

        // ===== EDUCATION =====
        form.education.forEach((edu, index) => {
            if (edu.id !== "") {
                formData.append(`education[${index}][id]`, edu.id);
            }
            formData.append(`education[${index}][school_or_cert]`, edu.school_or_cert);
            formData.append(`education[${index}][degree]`, edu.degree);
            formData.append(`education[${index}][field]`, edu.field);
            formData.append(`education[${index}][start_year]`, edu.start_year);
            formData.append(`education[${index}][end_year]`, edu.end_year);
            formData.append(`education[${index}][description]`, edu.description);
        });

        dispatch(editResume({ token, formData }))
            .unwrap()
            .then(() => {
                toast.success("Resume Updated Successfully!");
                navigate("/panel/resume-list");
            })
            .catch(() => {
                toast.error("Failed to update resume");
            });
    };


    if (loading) return <p>Loading resume...</p>;
    if (error) return <p className="text-danger">{error}</p>;

    /* ================= UI (SAME DESIGN AS ADD) ================= */
    return (
        <div className="col-md-8 col-xl-9 mt-4 mt-md-0">
            <div className="ps-2 ps-lg-4">
                <h4 className="fw-bold text-primary mb-4 border-bottom pb-3">
                    Edit Resume
                </h4>

                <form onSubmit={handleSubmit}>
                    {/* ===== BASIC INFO ===== */}
                    <div className="card shadow-sm mb-4">
                        <div className="card-body">
                            <h5 className="text-muted mb-3">Basic Information</h5>

                            <div className="row g-3">
                                <div className="col-md-6">
                                    <label className="form-label">Title</label>
                                    <input
                                        type="text"
                                        name="title"
                                        className="form-control"
                                        value={form.title}
                                        onChange={handleChange}
                                    />
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label">Headline</label>
                                    <input
                                        type="text"
                                        name="headline"
                                        className="form-control"
                                        value={form.headline}
                                        onChange={handleChange}
                                    />
                                </div>

                                <div className="col-md-12">
                                    <label className="form-label">Summary</label>
                                    <textarea
                                        name="summary"
                                        className="form-control"
                                        rows="3"
                                        value={form.summary}
                                        onChange={handleChange}
                                    />
                                </div>

                                <div className="col-md-12">
                                    <label className="form-label">Skills</label>
                                    <input
                                        type="text"
                                        name="skills"
                                        className="form-control"
                                        value={form.skills}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ===== EXPERIENCE & EDUCATION ===== */}
                    <div className="card shadow-sm mb-4">
                        <div className="card-body">
                            <h5 className="text-muted mb-3">
                                Work Experience
                                <button type="button" className="btn btn-sm btn-light-header ms-2" onClick={addExperience}>
                                    + Add
                                </button>
                            </h5>

                            {form.experience.map((exp, index) => (
                                <div key={index} className="border rounded p-3 mb-3">
                                    <div className="row g-3">

                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <h6 className="mb-0"></h6>
                                            {(form.experience.length > 1 && index != 0) && (
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-danger"
                                                    onClick={() => removeExperience(index)}
                                                >
                                                    ✕ Remove
                                                </button>
                                            )}
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label">Company</label>
                                            <input
                                                type="text"
                                                name="company"
                                                className="form-control"
                                                value={exp.company || ""}
                                                onChange={(e) => handleExperienceChange(index, e)}
                                            />
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label">Job Title</label>
                                            <input
                                                type="text"
                                                name="job_title"
                                                className="form-control"
                                                value={exp.job_title || ""}
                                                onChange={(e) => handleExperienceChange(index, e)}
                                            />
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label">Start Date</label>
                                            <input
                                                type="date"
                                                name="start_date"
                                                className="form-control"
                                                value={exp.start_date || ""}
                                                onChange={(e) => handleExperienceChange(index, e)}
                                            />
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label">End Date</label>
                                            <input
                                                type="date"
                                                name="end_date"
                                                className="form-control"
                                                value={exp.end_date || ""}
                                                onChange={(e) => handleExperienceChange(index, e)}
                                            />
                                        </div>

                                        <div className="col-md-12">
                                            <label className="form-label">Description</label>
                                            <textarea
                                                name="description"
                                                className="form-control"
                                                rows="2"
                                                value={exp.description || ""}
                                                onChange={(e) => handleExperienceChange(index, e)}
                                            />
                                        </div>

                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>


                    <div className="card shadow-sm mb-4">
                        <div className="card-body">
                            <h5 className="text-muted mb-3">
                                Education & Certifications
                                <button type="button" className="btn btn-sm btn-light-header ms-2" onClick={addEducation}>
                                    + Add
                                </button>
                            </h5>

                            {form.education.map((edu, index) => (
                                <div key={index} className="border rounded p-3 mb-3">
                                    <div className="row g-3">

                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <h6 className="mb-0"></h6>
                                            {(form.education.length > 1 && index != 0) && (
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-danger"
                                                    onClick={() => removeEducation(index)}
                                                >
                                                    ✕ Remove
                                                </button>
                                            )}
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label">Institute / Certification</label>
                                            <input
                                                type="text"
                                                name="school_or_cert"
                                                className="form-control"
                                                value={edu.school_or_cert || ""}
                                                onChange={(e) => handleEducationChange(index, e)}
                                            />
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label">Degree</label>
                                            <input
                                                type="text"
                                                name="degree"
                                                className="form-control"
                                                value={edu.degree || ""}
                                                onChange={(e) => handleEducationChange(index, e)}
                                            />
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label">Field of Study</label>
                                            <input
                                                type="text"
                                                name="field"
                                                className="form-control"
                                                value={edu.field || ""}
                                                onChange={(e) => handleEducationChange(index, e)}
                                            />
                                        </div>

                                        <div className="col-md-3">
                                            <label className="form-label">Start Year</label>
                                            <input
                                                type="number"
                                                name="start_year"
                                                className="form-control"
                                                value={edu.start_year || ""}
                                                onChange={(e) => handleEducationChange(index, e)}
                                            />
                                        </div>

                                        <div className="col-md-3">
                                            <label className="form-label">End Year</label>
                                            <input
                                                type="number"
                                                name="end_year"
                                                className="form-control"
                                                value={edu.end_year || ""}
                                                onChange={(e) => handleEducationChange(index, e)}
                                            />
                                        </div>

                                        <div className="col-md-12">
                                            <label className="form-label">Description</label>
                                            <textarea
                                                name="description"
                                                className="form-control"
                                                rows="2"
                                                value={edu.description || ""}
                                                onChange={(e) => handleEducationChange(index, e)}
                                            />
                                        </div>

                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>


                    <div className="text-end">
                        <button type="submit" className="btn btn-primary px-4">
                            Update Resume
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default WorkerEditResume;
